import { useEffect, useRef, useState } from 'react';
import type { CameraAdapter } from '../data/camera';
import type { MicrophoneAdapter } from '../data/microphone';
import { isWebcamKind, type Class, type ProjectKind, type Sample } from '../data/types';
import { audioFileToSampleBlobs } from '../ml/audioFeatures';
import { audioMinForClass } from '../ml/audioTrainer';
import { MIN_SAMPLES_PER_CLASS } from '../ml/trainer';
import { capture } from '../lib/analytics';
import { CaptureSettingsDialog } from './CaptureSettingsDialog';
import { effectiveSettings } from './captureSettings';
import type { ClassCaptureSettings } from '../data/types';
import { buildClassZipFilename, packClassSamples } from './sampleDownload';
import { ClipChip } from './ClipChip';
import { MicPanel } from './MicPanel';
import { SampleThumb } from './SampleThumb';
import { WebcamPanel } from './WebcamPanel';
import './ClassCard.css';

const MAX_CLASS_NAME_LENGTH = 40;

export interface ClassCardProps {
  cls: Class;
  /** Project kind drives whether this card captures images or audio. */
  kind: ProjectKind;
  samples: Sample[];
  samplesLoading: boolean;
  samplesError: string | null;
  onRename: (id: string, name: string) => void | Promise<void>;
  /** Persists the gear-icon settings dialog. */
  onUpdateCaptureSettings?: (id: string, settings: ClassCaptureSettings) => void | Promise<void>;
  /** Toggles whether this class participates in training / prediction. */
  onToggleDisabled?: (id: string, disabled: boolean) => void | Promise<void>;
  /** Soft-delete the class. Caller is responsible for the undo toast. */
  onDelete: (id: string) => void;
  /** Soft-delete every sample in this class. Caller renders the undo toast. */
  onRemoveAllSamples: (classId: string) => void;
  onAddSample: (
    classId: string,
    blob: Blob,
    opts?: { poseKeypoints?: ReadonlyArray<import('../data/types').SamplePoseKeypoint> },
  ) => void | Promise<void>;
  onDeleteSample: (classId: string, sampleId: string) => void | Promise<void>;
  /** Bubbles a thumbnail click up to the page so it can open the
   *  SampleModal (with prev/next + reassign). */
  onOpenSample?: (classId: string, sampleId: string) => void;
  /** For tests; production WebcamPanel uses the real browser adapter. */
  camera?: CameraAdapter;
  /** For tests; production MicPanel uses the real browser adapter. */
  microphone?: MicrophoneAdapter;
}

export function ClassCard({
  cls,
  kind,
  samples,
  samplesLoading,
  samplesError,
  onRename,
  onUpdateCaptureSettings,
  onToggleDisabled,
  onDelete,
  onRemoveAllSamples,
  onAddSample,
  onDeleteSample,
  onOpenSample,
  camera,
  microphone,
}: ClassCardProps): JSX.Element {
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(cls.name);
  // Pose projects auto-open the webcam on the *first* class card so
  // MoveNet starts running the moment the kid sees the project — they
  // get a live skeleton tracking their movement before clicking
  // record, which sets expectations and lets them frame themselves
  // first. We do NOT auto-open every class card: opening N webcams at
  // once on a multi-class project would spawn N MediaStream tracks
  // from one camera and run N MoveNet loops in parallel, which a $200
  // Chromebook can't handle. Other class cards stay opt-in.
  const [webcamOpen, setWebcamOpen] = useState(kind === 'pose' && cls.position === 0);
  const [micOpen, setMicOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [audioUploadError, setAudioUploadError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const settings = effectiveSettings(cls, kind);
  const captureOpen = (kind === 'audio' && micOpen) || (isWebcamKind(kind) && webcamOpen);

  useEffect(() => {
    if (editingName) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [editingName]);

  useEffect(() => {
    if (!editingName) setDraftName(cls.name);
  }, [cls.name, editingName]);

  const commitRename = (): void => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== cls.name) {
      void onRename(cls.id, trimmed);
    }
    setEditingName(false);
  };

  const handleFiles = async (fileList: FileList | null): Promise<void> => {
    if (!fileList) return;
    let added = 0;
    for (const file of Array.from(fileList)) {
      if (!file.type.startsWith('image/')) continue;
      await onAddSample(cls.id, file);
      added++;
    }
    if (added > 0) capture('samples_added', { source: 'upload', count: added });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadSamples = async (): Promise<void> => {
    if (samples.length === 0 || downloading) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const blob = await packClassSamples(cls.name, kind, samples);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildClassZipFilename(cls.name);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Could not download those samples.');
    } finally {
      setDownloading(false);
    }
  };

  const handleAudioFiles = async (fileList: FileList | null): Promise<void> => {
    if (!fileList) return;
    setAudioUploadError(null);
    let added = 0;
    try {
      for (const file of Array.from(fileList)) {
        // Be permissive about MIME — Chromebooks sometimes report empty
        // type for unfamiliar extensions, but the decoder will reject
        // anything it can't handle.
        if (file.type && !file.type.startsWith('audio/')) continue;
        const blobs = await audioFileToSampleBlobs(file);
        for (const blob of blobs) {
          await onAddSample(cls.id, blob);
          added++;
        }
      }
    } catch (err) {
      setAudioUploadError(err instanceof Error ? err.message : "Couldn't read that audio file.");
    } finally {
      if (added > 0) capture('samples_added', { source: 'upload', count: added });
      if (audioInputRef.current) audioInputRef.current.value = '';
    }
  };

  return (
    <article
      className={`bl-card-class${cls.disabled ? ' bl-card-class--disabled' : ''}`}
      aria-label={`Class ${cls.name}`}
      data-disabled={cls.disabled ? 'true' : undefined}
    >
      {cls.disabled && (
        <div className="bl-card-class__disabled-banner" role="status" aria-live="polite">
          This class is off. It isn’t part of training or testing right now.
        </div>
      )}
      <header className="bl-card-class__header">
        {editingName ? (
          <input
            ref={nameInputRef}
            type="text"
            className="bl-card-class__name-input"
            value={draftName}
            maxLength={MAX_CLASS_NAME_LENGTH}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') {
                setDraftName(cls.name);
                setEditingName(false);
              }
            }}
            aria-label={`Rename class ${cls.name}`}
          />
        ) : (
          <button
            type="button"
            className="bl-card-class__name"
            onClick={() => setEditingName(true)}
            aria-label={`Rename class ${cls.name}`}
          >
            {cls.name}
          </button>
        )}
        <ClassCount kind={kind} cls={cls} count={samples.length} />
        {onUpdateCaptureSettings && (
          <button
            type="button"
            className="bl-card-class__gear"
            onClick={() => setSettingsOpen((v) => !v)}
            aria-label={`Capture settings for ${cls.name}`}
            aria-expanded={settingsOpen}
          >
            <span aria-hidden="true">⚙︎</span>
          </button>
        )}
        <button
          type="button"
          className="bl-card-class__delete"
          onClick={() => onDelete(cls.id)}
          aria-label={`Delete class ${cls.name}`}
        >
          Delete
        </button>
      </header>

      {samplesError && (
        <p role="alert" className="bl-card-class__error">
          {samplesError}
        </p>
      )}

      {/* When the kid has a capture panel open, give it the full width
          of the card so the recording UI / live spectrogram / preview
          audio / Extract action have room to breathe. The samples row
          slides below in this state. The default state — no capture
          panel — uses a horizontal flow: capture buttons stack on the
          left, thumbnails wrap-as-row on the right with horizontal
          overflow, so adding more clips never pushes the train panel
          below the fold. */}
      <div
        className={`bl-card-class__body${
          captureOpen || settingsOpen ? ' bl-card-class__body--capture-open' : ''
        }`}
      >
        <div className="bl-card-class__capture-col">
          {settingsOpen && onUpdateCaptureSettings ? (
            <CaptureSettingsDialog
              cls={cls}
              kind={kind}
              onSave={async (next) => {
                await onUpdateCaptureSettings(cls.id, next);
                setSettingsOpen(false);
              }}
              {...(onToggleDisabled
                ? {
                    onToggleDisabled: async (disabled) => {
                      await onToggleDisabled(cls.id, disabled);
                    },
                  }
                : {})}
              onCancel={() => setSettingsOpen(false)}
            />
          ) : kind === 'audio' && micOpen ? (
            <MicPanel
              {...(microphone ? { microphone } : {})}
              recordSeconds={settings.durationSeconds}
              minimum={audioMinForClass(cls)}
              onCapture={(blob) => {
                capture('samples_added', { source: 'mic', count: 1 });
                return onAddSample(cls.id, blob);
              }}
              onClose={() => setMicOpen(false)}
            />
          ) : isWebcamKind(kind) && webcamOpen ? (
            <WebcamPanel
              {...(camera ? { camera } : {})}
              fps={settings.fps}
              holdToRecord={settings.holdToRecord}
              delaySeconds={settings.delaySeconds}
              durationSeconds={settings.durationSeconds}
              showPoseSkeleton={kind === 'pose'}
              onCapture={(blob, opts) => {
                capture('samples_added', { source: 'webcam', count: 1 });
                return onAddSample(cls.id, blob, opts);
              }}
              onClose={() => setWebcamOpen(false)}
            />
          ) : kind === 'audio' ? (
            <>
              <button
                type="button"
                className="bl-card-class__capture-button"
                onClick={() => setMicOpen(true)}
              >
                Use mic
              </button>
              <button
                type="button"
                className="bl-card-class__capture-button"
                onClick={() => audioInputRef.current?.click()}
              >
                Upload audio
              </button>
              <input
                ref={audioInputRef}
                type="file"
                accept="audio/*,.wav,.mp3,.m4a,.ogg,.webm"
                multiple
                className="bl-card-class__file-input"
                onChange={(e) => void handleAudioFiles(e.target.files)}
                aria-label={`Upload audio for ${cls.name}`}
              />
              {audioUploadError && (
                <p role="alert" className="bl-card-class__error">
                  {audioUploadError}
                </p>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="bl-card-class__capture-button"
                onClick={() => setWebcamOpen(true)}
              >
                Use camera
              </button>
              <button
                type="button"
                className="bl-card-class__capture-button"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload pictures
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="bl-card-class__file-input"
                onChange={(e) => void handleFiles(e.target.files)}
                aria-label={`Upload pictures for ${cls.name}`}
              />
            </>
          )}
        </div>
        <SamplesRegion
          cls={cls}
          kind={kind}
          samples={samples}
          samplesLoading={samplesLoading}
          onDeleteSample={onDeleteSample}
          onRemoveAllSamples={onRemoveAllSamples}
          onDownloadSamples={() => void handleDownloadSamples()}
          downloading={downloading}
          downloadError={downloadError}
          {...(onOpenSample ? { onOpenSample: (id: string) => onOpenSample(cls.id, id) } : {})}
          // Layout follows the capture column. Open = fixed-width
          // camera/settings on the left, multi-row scrolling grid on
          // the right (so 4+ thumbs sit beside the viewfinder).
          // Closed = TM-style single horizontal strip with the action
          // buttons fixed on the left and the thumbnails scrolling
          // horizontally so adding more samples never pushes the
          // train panel down.
          layout={captureOpen || settingsOpen ? 'grid' : 'row'}
        />
      </div>
    </article>
  );
}

interface SamplesRegionProps {
  cls: Class;
  kind: ProjectKind;
  samples: Sample[];
  samplesLoading: boolean;
  onDeleteSample: (classId: string, sampleId: string) => void | Promise<void>;
  onRemoveAllSamples: (classId: string) => void;
  /** Optional download-zip handler. Hidden when not provided. */
  onDownloadSamples?: () => void | Promise<void>;
  downloading?: boolean;
  downloadError?: string | null;
  /** Optional open-sample handler. When provided, thumbnails / chips
   *  become clickable to open the SampleModal. */
  onOpenSample?: (sampleId: string) => void;
  /** `row` = closed-state: single horizontal strip, scrolls
   *  horizontally so action buttons stay glued to the left.
   *  `grid` = open-state: multi-row grid that wraps + scrolls
   *  vertically so 4+ thumbs sit next to the camera viewfinder. */
  layout: 'row' | 'grid';
}

function SamplesRegion({
  cls,
  kind,
  samples,
  samplesLoading,
  onDeleteSample,
  onRemoveAllSamples,
  onDownloadSamples,
  downloading,
  downloadError,
  onOpenSample,
  layout,
}: SamplesRegionProps): JSX.Element {
  const isLoading = samplesLoading && samples.length === 0;
  const isEmpty = !isLoading && samples.length === 0;

  return (
    <div className={`bl-card-class__samples bl-card-class__samples--${layout}`}>
      {isLoading && (
        <p className="bl-card-class__loading" aria-live="polite">
          {kind === 'audio' ? 'Loading clips…' : 'Loading samples…'}
        </p>
      )}
      {isEmpty && (
        <p className="bl-card-class__empty">
          {kind === 'audio'
            ? 'No clips yet. Use the mic to record some.'
            : 'No samples yet. Use the camera or upload some.'}
        </p>
      )}
      {!isLoading && !isEmpty && (
        <>
          {kind === 'audio' ? (
            <div className="bl-card-class__chips" role="list" aria-label={`Clips for ${cls.name}`}>
              {samples.map((sample, index) => (
                <div role="listitem" key={sample.id}>
                  <ClipChip
                    sample={sample}
                    index={index}
                    onDelete={(id) => onDeleteSample(cls.id, id)}
                    {...(onOpenSample ? { onOpen: onOpenSample } : {})}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="bl-card-class__grid" role="list" aria-label={`Samples for ${cls.name}`}>
              {samples.map((sample, index) => (
                <div role="listitem" key={sample.id}>
                  <SampleThumb
                    sample={sample}
                    index={index}
                    onDelete={(id) => onDeleteSample(cls.id, id)}
                    {...(onOpenSample ? { onOpen: onOpenSample } : {})}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="bl-card-class__sample-actions">
            <button
              type="button"
              className="bl-card-class__remove-all"
              onClick={() => onRemoveAllSamples(cls.id)}
              aria-label={
                kind === 'audio'
                  ? `Remove all clips in ${cls.name}`
                  : `Remove all samples in ${cls.name}`
              }
            >
              {kind === 'audio' ? 'Remove all clips' : 'Remove all samples'}
            </button>
            {onDownloadSamples && (
              <button
                type="button"
                className="bl-card-class__download"
                onClick={() => void onDownloadSamples()}
                disabled={downloading}
                aria-label={
                  kind === 'audio'
                    ? `Download clips in ${cls.name} as a zip`
                    : `Download samples in ${cls.name} as a zip`
                }
              >
                {downloading ? 'Zipping…' : 'Download'}
              </button>
            )}
          </div>
          {downloadError && (
            <p role="alert" className="bl-card-class__error">
              {downloadError}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function countNoun(kind: ProjectKind, n: number): string {
  if (kind === 'audio') return n === 1 ? 'clip' : 'clips';
  return n === 1 ? 'sample' : 'samples';
}

interface ClassCountProps {
  kind: ProjectKind;
  cls: Class;
  count: number;
}

/**
 * Per-class header counter — TM-style "X / Y minimum" so the kid can see the
 * threshold for each class without hunting through the train panel. Below
 * the minimum the count is rendered in muted copy; once the class meets the
 * threshold it switches to a confident "X clips" with a green check.
 */
function ClassCount({ kind, cls, count }: ClassCountProps): JSX.Element {
  const min = kind === 'audio' ? audioMinForClass(cls) : MIN_SAMPLES_PER_CLASS;
  const noun = countNoun(kind, count);
  const met = count >= min;
  return (
    <span
      className={`bl-card-class__count${met ? ' bl-card-class__count--met' : ''}`}
      aria-label={`${count} ${noun}, ${min} minimum`}
    >
      {met ? (
        <span aria-hidden="true" className="bl-card-class__count-check">
          ✓
        </span>
      ) : null}
      <span className="bl-card-class__count-value">{count}</span>
      <span className="bl-card-class__count-sep">/</span>
      <span className="bl-card-class__count-min">{min} minimum</span>
    </span>
  );
}
